import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { FileText, Download, Eye, RefreshCw, Info, Wallet, FileSignature, Clock } from 'lucide-react';
import { ESS_COLORS } from './constants';

export function HistoriqueTab({ ctx }) {
  const { simulations, handleViewAttestation, handleDownloadPDF } = ctx;
  return (
        <TabsContent value="historique">
          <Card>
            <CardHeader>
              <CardTitle>Historique des simulations DOS</CardTitle>
              <CardDescription>Simulations individuelles et collectives sauvegardées</CardDescription>
            </CardHeader>
            <CardContent>
              {simulations.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Aucune simulation sauvegardée</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {simulations.map((sim) => (
                    <div key={sim.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <Badge className="bg-emerald-700 text-white font-semibold">
                            DOS-{sim.attestation_id}
                          </Badge>
                          <span className="font-medium capitalize">
                            {sim.projet?.type_projet || 'N/A'}
                          </span>
                          <span className="text-gray-500">•</span>
                          <span className="text-gray-600">{sim.calcul?.territoire_config?.nom || sim.projet?.territoire}</span>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                          <span>{sim.calcul?.montant_travaux_ht?.toLocaleString('fr-FR')} € (Montant de base)</span>
                          <span>→</span>
                          <span className="font-semibold text-emerald-600">
                            {sim.calcul?.contribution_dos?.toLocaleString('fr-FR')} € DOS
                          </span>
                          <span className="text-gray-400">
                            {new Date(sim.created_at).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => handleViewAttestation(sim.id)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDownloadPDF(sim.id)}>
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
  );
}

export function FondsTab({ ctx }) {
  const { fondsDashboard, decennaleDashboard } = ctx;
  return (
          <TabsContent value="fonds">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-emerald-600" />
                    Dashboard Fonds de Garantie DOS
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {fondsDashboard ? (
                    <div className="space-y-6">
                      {/* KPIs */}
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-emerald-50 p-4 rounded-xl">
                          <div className="text-sm text-emerald-600">Fonds disponible</div>
                          <div className="text-2xl font-bold text-emerald-700">
                            {fondsDashboard.fonds.fonds_disponible?.toLocaleString('fr-FR')} €
                          </div>
                        </div>
                        <div className="bg-blue-50 p-4 rounded-xl">
                          <div className="text-sm text-blue-600">Total contributions</div>
                          <div className="text-2xl font-bold text-blue-700">
                            {fondsDashboard.fonds.total_contributions?.toLocaleString('fr-FR')} €
                          </div>
                        </div>
                        <div className="bg-amber-50 p-4 rounded-xl">
                          <div className="text-sm text-amber-600">Nb projets</div>
                          <div className="text-2xl font-bold text-amber-700">
                            {fondsDashboard.statistiques.nb_projets}
                          </div>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-xl">
                          <div className="text-sm text-purple-600">Taux couverture</div>
                          <div className="text-2xl font-bold text-purple-700">
                            {fondsDashboard.statistiques.taux_couverture}%
                          </div>
                        </div>
                      </div>
                      
                      {/* Par territoire */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-sm">Par territoire DOM</CardTitle>
                          </CardHeader>
                          <CardContent>
                            {Object.entries(fondsDashboard.par_territoire || {}).length > 0 ? (
                              <div className="space-y-2">
                                {Object.entries(fondsDashboard.par_territoire).map(([territoire, data]) => (
                                  <div key={territoire} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                    <span className="capitalize">{territoire.replace('_', ' ')}</span>
                                    <div className="text-right">
                                      <span className="font-semibold">{data.contributions?.toLocaleString('fr-FR')} €</span>
                                      <span className="text-gray-400 text-sm ml-2">({data.count})</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-500 text-center py-4">Aucune donnée</p>
                            )}
                          </CardContent>
                        </Card>
                        
                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-sm">Par type de projet</CardTitle>
                          </CardHeader>
                          <CardContent>
                            {Object.entries(fondsDashboard.par_type_projet || {}).length > 0 ? (
                              <div className="space-y-2">
                                {Object.entries(fondsDashboard.par_type_projet).map(([type, data]) => (
                                  <div key={type} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                    <span className="capitalize">{type}</span>
                                    <div className="text-right">
                                      <span className="font-semibold">{data.contributions?.toLocaleString('fr-FR')} €</span>
                                      <span className="text-gray-400 text-sm ml-2">({data.count})</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-500 text-center py-4">Aucune donnée</p>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <RefreshCw className="h-8 w-8 mx-auto text-gray-400 animate-spin" />
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Dashboard Décennale */}
              {decennaleDashboard && (
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-amber-600" />
                      Dashboard Décennale Multi-lots
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-amber-50 p-4 rounded-xl">
                        <div className="text-sm text-amber-600">Programmes</div>
                        <div className="text-2xl font-bold text-amber-700">
                          {decennaleDashboard.global?.total_programmes || 0}
                        </div>
                      </div>
                      <div className="bg-green-50 p-4 rounded-xl">
                        <div className="text-sm text-green-600">Actifs</div>
                        <div className="text-2xl font-bold text-green-700">
                          {decennaleDashboard.global?.programmes_actifs || 0}
                        </div>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-xl">
                        <div className="text-sm text-blue-600">Total lots</div>
                        <div className="text-2xl font-bold text-blue-700">
                          {decennaleDashboard.global?.total_lots || 0}
                        </div>
                      </div>
                      <div className="bg-red-50 p-4 rounded-xl">
                        <div className="text-sm text-red-600">Désordres</div>
                        <div className="text-2xl font-bold text-red-700">
                          {decennaleDashboard.global?.total_desordres || 0}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
  );
}

export function SignatureTab() {
  return (
          <TabsContent value="signature">
            <div className="space-y-6">
              {/* En-tête Signature */}
              <Card className="border-2" style={{ borderColor: ESS_COLORS.bleuInstitutionnel }}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: ESS_COLORS.bleuInstitutionnel }}>
                      <FileSignature className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <div className="text-xl">Workflow DOMIUM</div>
                      <div className="text-sm font-normal text-gray-500">
                        Signature eIDAS (SEA) • PAdES-LT/LTA • Archivage GED probant
                      </div>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="p-4 rounded-xl text-center" style={{ backgroundColor: ESS_COLORS.sableClair }}>
                      <div className="text-3xl font-bold" style={{ color: ESS_COLORS.vertPrincipal }}>SEA</div>
                      <div className="text-sm text-gray-600">Niveau signature</div>
                    </div>
                    <div className="p-4 rounded-xl text-center" style={{ backgroundColor: ESS_COLORS.sableClair }}>
                      <div className="text-3xl font-bold" style={{ color: ESS_COLORS.bleuInstitutionnel }}>PAdES</div>
                      <div className="text-sm text-gray-600">Format PDF</div>
                    </div>
                    <div className="p-4 rounded-xl text-center" style={{ backgroundColor: ESS_COLORS.sableClair }}>
                      <div className="text-3xl font-bold" style={{ color: ESS_COLORS.vertPrincipal }}>10 ans</div>
                      <div className="text-sm text-gray-600">Archivage</div>
                    </div>
                    <div className="p-4 rounded-xl text-center" style={{ backgroundColor: ESS_COLORS.sableClair }}>
                      <div className="text-3xl font-bold" style={{ color: ESS_COLORS.statutActif }}>QR</div>
                      <div className="text-sm text-gray-600">Vérification</div>
                    </div>
                  </div>
                  
                  {/* Workflow visuel */}
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <h4 className="font-semibold text-gray-700 mb-4">États du workflow</h4>
                    <div className="flex items-center justify-between overflow-x-auto pb-2">
                      {['DRAFT', 'PDF', 'SIGN', 'LTV', 'GED', 'QR'].map((step, idx) => (
                        <div key={step} className="flex items-center">
                          <div className="flex flex-col items-center">
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                              style={{ backgroundColor: idx === 0 ? ESS_COLORS.vertPrincipal : '#D1D5DB' }}
                            >
                              {idx + 1}
                            </div>
                            <span className="text-xs mt-1 text-gray-600">{step}</span>
                          </div>
                          {idx < 5 && (
                            <div className="w-8 h-0.5 bg-gray-300 mx-1" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Ordre de signature */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Ordre de signature recommandé</CardTitle>
                  <CardDescription>Signataires pour Attestation DOS / CP-DOS</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-4 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: ESS_COLORS.vertPrincipal }}>1</div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-800">Maître d&apos;ouvrage (MOA)</div>
                        <div className="text-sm text-gray-500">Signataire principal</div>
                      </div>
                      <Badge style={{ backgroundColor: ESS_COLORS.vertPrincipal }} className="text-white">Obligatoire</Badge>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">2</div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-800">Constructeur / Entreprise</div>
                        <div className="text-sm text-gray-500">Signataire technique (si CP-DOS)</div>
                      </div>
                      <Badge variant="outline" className="border-blue-500 text-blue-700">Optionnel</Badge>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-xl border border-purple-200">
                      <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold">3</div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-800">COPPAM</div>
                        <div className="text-sm text-gray-500">Cachet / Visa organisation</div>
                      </div>
                      <Badge variant="outline" className="border-purple-500 text-purple-700">Scellement</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Documents signables */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Documents signables (MVP)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 border-2 border-emerald-300 rounded-xl bg-emerald-50">
                      <FileText className="h-8 w-8 mb-2" style={{ color: ESS_COLORS.vertPrincipal }} />
                      <div className="font-semibold">Attestation DOS</div>
                      <div className="text-sm text-gray-500">Obligatoire</div>
                    </div>
                    <div className="p-4 border-2 border-blue-300 rounded-xl bg-blue-50">
                      <FileText className="h-8 w-8 mb-2 text-blue-600" />
                      <div className="font-semibold">CP-DOS</div>
                      <div className="text-sm text-gray-500">Conditions Particulières</div>
                    </div>
                    <div className="p-4 border-2 border-amber-300 rounded-xl bg-amber-50">
                      <FileText className="h-8 w-8 mb-2 text-amber-600" />
                      <div className="font-semibold">Annexe Passerelle</div>
                      <div className="text-sm text-gray-500">Si CCMI↔DOS</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Info provider */}
              <div className="p-4 rounded-xl" style={{ backgroundColor: `${ESS_COLORS.bleuInstitutionnel}10`, border: `1px solid ${ESS_COLORS.bleuInstitutionnel}30` }}>
                <div className="flex items-center gap-3">
                  <Info className="h-5 w-5" style={{ color: ESS_COLORS.bleuInstitutionnel }} />
                  <div className="text-sm" style={{ color: ESS_COLORS.bleuInstitutionnel }}>
                    <strong>Provider eIDAS :</strong> Yousign (SEA) • Signature PDF PAdES-LT/LTA • 
                    Archivage GED probant 10 ans • QR public de vérification
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
  );
}
